import type { NotificationData } from '../../services/notification.service';
import type {
  ModalStateService,
  OpenNotificationModalOptions,
} from '../../services/modal-state.service';
import type { WayPurchaseModalStateService } from '../../components/way-purchase-modal/services/way-purchase-modal-state.service';
import type { MultiEligibleSelectionModalStateService } from '../../components/multi-eligible-selection-modal/services/multi-eligible-selection-modal-state.service';
import type { LoggerService } from '../../services/logger.service';
import type { Package } from '../types';
import { applyResolvedLprEntryTypeToNotification } from '../../notifications/notification.utils';
import {
  isPrepaidRedeemLprNotification,
  normalizePrepaidRedeemNotificationForModal,
} from './get-lp-image-prepaid-redeem.util';
import { isManualLprCaptureNotification } from './notification-display.util';
import type { ActiveSubscriptionGateService } from '../../services/active-subscription-gate.service';
import { openRoutedNotificationModal } from '../../components/way-purchase-modal/utils/way-purchase-modal-open.util';

/**
 * Staff header "+" / manual counter sale — same order-request UI; POST /order uses order_source `saas`.
 */
export function buildStaffQuickSaleNotification(): NotificationData {
  return {
    type: 'create-order-request',
    message: 'New order',
    timestamp: new Date(),
    data: { quick_plus_order: true },
  };
}

/**
 * Sales "Buy package" — same order-request UI with package pre-selected; order_source `saas`.
 */
export function buildBuyPackageNotification(
  packageData: Package,
  washType: string | undefined
): NotificationData {
  return {
    type: 'create-order-request',
    message: 'Buy Package',
    timestamp: new Date(),
    data: {
      packageId: packageData.id,
      package_id: packageData.id,
      package_name: packageData.package_name,
      wash_type: washType,
      washType,
      isBuyPackageFlow: true,
    },
  };
}

/** Pusher lane events — respect queue/suppression; routes Way / multi-eligible types. */
export function openPusherNotificationModal(
  modalStateService: ModalStateService,
  wayPurchaseModalState: WayPurchaseModalStateService,
  notification: NotificationData,
  activeSubscriptionGate?: ActiveSubscriptionGateService,
  logger?: LoggerService,
  multiEligibleModalState?: MultiEligibleSelectionModalStateService
): void {
  const row = (notification.data ?? {}) as Record<string, any>;
  const normalized = normalizePrepaidRedeemNotificationForModal(
    applyResolvedLprEntryTypeToNotification(notification, row)
  );
  // Multi-eligible, UI image scans, and prepaid redeem must replace any open dialog —
  // otherwise an in-place update skips form reset + prepaid status bootstrap (stuck loading).
  const forceReplace =
    isManualLprCaptureNotification(normalized) ||
    normalized.type === 'multi-eligible-selection' ||
    isPrepaidRedeemLprNotification(normalized.type, normalized.data as Record<string, any>);
  openRoutedNotificationModal(
    modalStateService,
    wayPurchaseModalState,
    {
      notification: normalized,
      behavior: forceReplace ? 'replace' : 'respect_state',
      shouldStore: true,
    },
    activeSubscriptionGate,
    logger,
    multiEligibleModalState
  );
}

/** Header "+" quick sale — force open, no history row. Closes Way if open. */
export function openStaffQuickSaleModal(
  modalStateService: ModalStateService,
  wayPurchaseModalState: WayPurchaseModalStateService,
  multiEligibleModalState?: MultiEligibleSelectionModalStateService
): void {
  openRoutedNotificationModal(
    modalStateService,
    wayPurchaseModalState,
    {
      notification: buildStaffQuickSaleNotification(),
      behavior: 'replace',
      shouldStore: false,
    },
    undefined,
    undefined,
    multiEligibleModalState
  );
}

/** Sales package picker — queue-friendly open, no history row. */
export function openBuyPackageModal(
  modalStateService: ModalStateService,
  wayPurchaseModalState: WayPurchaseModalStateService,
  notification: NotificationData,
  multiEligibleModalState?: MultiEligibleSelectionModalStateService
): void {
  openRoutedNotificationModal(
    modalStateService,
    wayPurchaseModalState,
    {
      notification,
      behavior: 'respect_state',
      shouldStore: false,
    },
    undefined,
    undefined,
    multiEligibleModalState
  );
}

/**
 * Notifications list / dropdown / detail view — user explicitly reopens a row.
 * Clears dismiss suppression so a dismissed LPR entry can open again.
 */
export function openUserReopenNotificationModal(
  modalStateService: ModalStateService,
  wayPurchaseModalState: WayPurchaseModalStateService,
  notification: NotificationData,
  activeSubscriptionGate?: ActiveSubscriptionGateService,
  multiEligibleModalState?: MultiEligibleSelectionModalStateService
): void {
  openRoutedNotificationModal(
    modalStateService,
    wayPurchaseModalState,
    {
      notification,
      behavior: 'replace',
      shouldStore: false,
      clearDismissSuppression: true,
    },
    activeSubscriptionGate,
    undefined,
    multiEligibleModalState
  );
}

/** After POST /order — swap to payment checkout without persisting a duplicate history entry. */
export function openPaymentCheckoutTransitionModal(
  modalStateService: ModalStateService,
  notification: NotificationData
): void {
  modalStateService.openNotificationModal({
    notification,
    behavior: 'replace',
    shouldStore: false,
    clearDismissSuppression: false,
  });
}

export type { OpenNotificationModalOptions };
